import { useState, useCallback } from 'react';

export function useScorecard({
    sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
    personalLoans, goldLoans,
    currentCycle, monthlySnapshots, setMonthlySnapshots,
    toBase,
}) {
    const [showScorecard, setShowScorecard] = useState(false);
    const [scorecardData, setScorecardData] = useState(null);

    // ── NG-3: Compute scorecard for the current period ────────────────
    const computeScorecard = useCallback(() => {
        const period = currentCycle.cycleId;
        const now = new Date().toISOString();

        // ── Budget Discipline Score ──
        const budgetSections = [
            { key: 'sgCycle1', label: '🇸🇬 SG Cycle 1', symbol: '$', items: sgBudgetCycle1 },
            { key: 'sgCycle2', label: '🇸🇬 SG Cycle 2', symbol: '$', items: sgBudgetCycle2 },
            { key: 'lkA', label: '🇱🇰 LK Budget A', symbol: '₨', items: lkBudgetDamith },
            { key: 'lkB', label: '🇱🇰 LK Budget B', symbol: '₨', items: lkBudgetShan },
        ];
        const hasActuals = budgetSections.some(s =>
            s.items.some(i => i.enabled && (i.actualSpent || 0) > 0)
        );
        let budgetScore = null;
        let budgetDetails = [];
        let totalBudgeted = 0;
        let totalSpent = 0;
        if (hasActuals) {
            let sectionScores = [];
            budgetSections.forEach(sec => {
                const enabled = sec.items.filter(i => i.enabled);
                const budget = enabled.reduce((s, i) => s + (i.amount || 0), 0);
                const spent = enabled.reduce((s, i) => s + (i.actualSpent || 0), 0);
                if (budget === 0) return;
                const pct = Math.round((spent / budget) * 100);
                const sectionScore = Math.max(0, Math.min(100, Math.round((1 - Math.max(0, spent - budget) / budget) * 100)));
                sectionScores.push(sectionScore);
                let status, statusColor;
                if (pct <= 90) { status = '✅ Under Budget'; statusColor = '#10B981'; }
                else if (pct <= 100) { status = '⚠️ Near Limit'; statusColor = '#F59E0B'; }
                else { status = '🔴 Over Budget'; statusColor = '#EF4444'; }
                budgetDetails.push({ label: sec.label, symbol: sec.symbol, budget, spent, pct, status, statusColor });
                totalBudgeted += (sec.symbol === '$') ? budget : 0;
                totalSpent += (sec.symbol === '$') ? spent : 0;
            });
            budgetScore = sectionScores.length > 0
                ? Math.round(sectionScores.reduce((a, b) => a + b, 0) / sectionScores.length)
                : null;
        }

        // ── Loan Repayment Discipline Score ──
        const today = new Date().toISOString().split('T')[0];
        const monthStr = today.slice(0, 7);
        let loanDetails = [];
        let loansMarkedPaid = 0;
        const allLoans = [
            ...personalLoans.map(l => ({ ...l, _type: 'personal' })),
            ...goldLoans.map(l => ({ ...l, _type: 'gold' })),
        ];
        allLoans.forEach(loan => {
            const history = Array.isArray(loan.paymentHistory) ? loan.paymentHistory : [];
            const thisMonthEntry = history.find(e => e.period === monthStr);
            const paid = !!thisMonthEntry;
            if (paid) loansMarkedPaid++;
            const dateStr = thisMonthEntry ? thisMonthEntry.date : null;
            const amount = thisMonthEntry ? thisMonthEntry.amount : (loan.monthlyInstallment || loan.monthlyInterest || 0);
            loanDetails.push({
                name: loan.name || loan.lender,
                paid,
                date: dateStr,
                amount,
                partial: thisMonthEntry && thisMonthEntry.type === 'partial',
                _type: loan._type,
            });
        });
        const loansTotal = allLoans.length;
        const loanScore = loansTotal > 0 ? Math.round((loansMarkedPaid / loansTotal) * 100) : null;

        // ── Net Cash Position (all amounts in base currency) ──
        const totalLoanRepayments = personalLoans.reduce((s, l) => s + toBase(l.monthlyInstallment || 0, l.currency || 'SGD'), 0);
        const totalOutflows = totalSpent + totalLoanRepayments;
        const netVariance = totalBudgeted - totalSpent;

        // ── Overall Score ──
        const scoreParts = [budgetScore, loanScore].filter(s => s !== null);
        const overallScore = scoreParts.length > 0
            ? Math.round(scoreParts.reduce((a, b) => a + b, 0) / scoreParts.length)
            : null;

        // ── Behavioural Insights ──
        const insights = [];
        if (budgetScore !== null) {
            if (budgetScore >= 90) insights.push({ emoji: '🟢', type: 'encourage', msg: `Great discipline! You scored ${budgetScore}/100 on budget adherence this month. Keep it up!` });
            else if (budgetScore >= 70) {
                const overBudget = budgetDetails.filter(d => d.pct > 100);
                if (overBudget.length > 0) insights.push({ emoji: '🟡', type: 'nudge', msg: `You overspent in ${overBudget.map(d => d.label).join(', ')}. Review those categories next month.` });
                else insights.push({ emoji: '🟡', type: 'nudge', msg: `You came close to your limits in some categories. A little more restraint will push your score into the green.` });
            }
            else insights.push({ emoji: '🔴', type: 'alert', msg: `Budget score is ${budgetScore}/100. Multiple categories exceeded their limits — review and adjust for next month.` });
        } else {
            insights.push({ emoji: '💡', type: 'info', msg: `Start logging actuals via the Log Spend button to unlock your Budget Score.` });
        }
        if (loanScore !== null) {
            const unpaid = loanDetails.filter(l => !l.paid);
            if (loanScore === 100) insights.push({ emoji: '🟢', type: 'encourage', msg: `All ${loansTotal} loan instalments recorded this month. Excellent financial discipline!` });
            else if (unpaid.length > 0) insights.push({ emoji: '🔴', type: 'alert', msg: `${unpaid.map(l => l.name).join(', ')} — payment not recorded. Mark paid if settled, or review your repayment plan.` });
        }
        const goldLoansWithInterest = goldLoans.filter(l => l.unpaidInterest > 0);
        if (goldLoansWithInterest.length > 0) {
            goldLoansWithInterest.forEach(gl => {
                insights.push({ emoji: '💎', type: 'gold-warn', msg: `Unpaid interest on ${gl.name || gl.lender} has reached ₨${Math.round(gl.unpaidInterest).toLocaleString()}. Regular payments prevent compounding.` });
            });
        }

        // ── Trend data from stored snapshots ──
        const recentSnapshots = [...monthlySnapshots].slice(0, 5).reverse();

        const snapshot = {
            period,
            generatedAt: now,
            budgetScore,
            loanScore,
            overallScore,
            totalBudgeted,
            totalSpent,
            loansMarkedPaid,
            loansTotal,
            netVariance,
        };

        // Save snapshot (upsert by period)
        setMonthlySnapshots(prev => {
            const existing = prev.findIndex(s => s.period === period);
            if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = snapshot;
                return updated;
            }
            return [snapshot, ...prev];
        });

        return {
            period, budgetScore, loanScore, overallScore, budgetDetails, loanDetails,
            totalBudgeted, totalSpent, totalLoanRepayments, totalOutflows, netVariance,
            insights, hasActuals, recentSnapshots, snapshot
        };
    }, [sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
        personalLoans, goldLoans, currentCycle, monthlySnapshots, toBase,
        setMonthlySnapshots]);

    // ── NG-3: Scorecard modal state — computed on open ───────────────
    const handleOpenScorecard = useCallback(() => {
        const data = computeScorecard();
        setScorecardData(data);
        setShowScorecard(true);
    }, [computeScorecard]);

    return { showScorecard, setShowScorecard, scorecardData, handleOpenScorecard };
}
